import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RenderError from './RenderError';
import {
  otherPropaneFueledProducts,
  topInformationSources,
  monthsToBuy
} from './config';
import Success from './Success';
import Submit from './Submit';

function NewEquipment({
  applicationData: {
    aid,
    otherPropaneProducts,
    topInfoSources,
    topMonthsToBuy,
    equipmentType
  },
  setApplicationData,
  initialView,
  setView
}) {
  const [formData, setFormData] = useState({
    aid: aid ?? '',
    equipmentType: equipmentType ?? '',
    otherPropaneFueledProducts: otherPropaneProducts ?? {},
    topInfoSources: topInfoSources ?? {},
    topMonthsToBuy: topMonthsToBuy ?? {}
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  // limit selections for questions 2 and 3
  useEffect(() => {
    if (
      Object.keys(formData.topInfoSources).length > 5 ||
      Object.keys(formData.topMonthsToBuy).length > 3
    ) {
      if (Object.keys(formData.topInfoSources).length > 5) {
        setErrors({
          ...errors,
          topInfoSources: 'Please only choose up to 5'
        });
      } else if (Object.keys(formData.topMonthsToBuy).length > 3) {
        setErrors({
          ...errors,
          topMonthsToBuy: 'Please only choose up to 3'
        });
      }
    } else {
      let newErrors = { ...errors };
      delete newErrors.topInfoSources;
      delete newErrors.topMonthsToBuy;
      setErrors(newErrors);
    }
  }, [formData]);

  const handleCheck = (event, question) => {
    const { value } = event.target;

    if (value in formData[question]) {
      let newFormData = formData[question];
      delete newFormData[value];
      setFormData({ ...formData, [question]: newFormData });
    } else {
      setFormData({
        ...formData,
        [question]: {
          ...formData[question],
          [value]: value === 'Other engine type (please specify)' ? '' : value
        }
      });
    }
  };

  const handleInputChange = (event) => {
    let { value, name } = event.target;

    if (name === 'otherPropaneFueledProducts_other') {
      setFormData({
        ...formData,
        otherPropaneFueledProducts: {
          ...formData.otherPropaneFueledProducts,
          ['Other (please specify)']: value
        }
      });
    } else if (name === 'topInfoSources_other') {
      setFormData({
        ...formData,
        topInfoSources: {
          ...formData.topInfoSources,
          ['Other (please specify)']: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    setSuccess('');
    setErrors([]);

    axios
      .post(
        window.wlPipConfig.ajaxUrl + '?action=wl_fip_save_new_equipment',
        formData
      )
      .then((res) => {
        setLoading(false);
        setSuccess(res.data.data.message);
        setApplicationData(res.data.data.applicationData);
        setTimeout(() => setView(initialView), 1000);
      })
      .catch((err) => {
        setLoading(false);
        setErrors(err.response.data.data);
      });
  };

  return (
    <div className="fip-unit-form">
      <div className="fip-unit-form-fields">
        <div className="grid__col-xs-12 u--padding-left-0">
          <div className="form__field form__field--required">
            <label className="form__label">
              What other propane-fueled products or appliances do you use at
              your place of business? (select all that apply)
            </label>
            {errors.otherPropaneFueledProducts && (
              <RenderError error={errors.otherPropaneFueledProducts} />
            )}
            {Object.values(otherPropaneFueledProducts).map((product) => (
              <div key={product}>
                <label className="form__checkbox">
                  <input
                    type="checkbox"
                    value={product}
                    checked={product in formData.otherPropaneFueledProducts}
                    onChange={(event) =>
                      handleCheck(event, 'otherPropaneFueledProducts')
                    }
                  />
                  {product}
                </label>
                {product in formData.otherPropaneFueledProducts &&
                  (product === 'Other (please specify)' ? (
                    <div className="u--margin-top-10 u--margin-left-10">
                      <input
                        type="text"
                        name="otherPropaneFueledProducts_other"
                        value={formData.otherPropaneFueledProducts[product]}
                        onChange={handleInputChange}
                        className="form__input form__input--lg u--width-100"
                      />
                      {errors[product] && (
                        <RenderError error={errors[product]} />
                      )}
                    </div>
                  ) : (
                    ''
                  ))}
              </div>
            ))}
          </div>
          <div className="form__field form__field--required">
            <label className="form__label">
              What are your top sources for information when looking to purchase
              new equipment? (select up to five)
            </label>
            {errors.topInfoSources && (
              <RenderError error={errors.topInfoSources} />
            )}
            {Object.values(topInformationSources).map((source) => (
              <div key={source}>
                <label className="form__checkbox">
                  <input
                    type="checkbox"
                    value={source}
                    checked={source in formData.topInfoSources}
                    onChange={(event) => handleCheck(event, 'topInfoSources')}
                  />
                  {source}
                </label>
                {source in formData.topInfoSources &&
                  (source === 'Other (please specify)' ? (
                    <div className="u--margin-top-10 u--margin-left-10">
                      <input
                        type="text"
                        name="topInfoSources_other"
                        value={formData.topInfoSources[source]}
                        onChange={handleInputChange}
                        className="form__input form__input--lg u--width-100"
                      />
                      {errors[source] && <RenderError error={errors[source]} />}
                    </div>
                  ) : (
                    ''
                  ))}
              </div>
            ))}
          </div>
          <div className="form__field form__field--required">
            <label className="form__label">
              Check the top three months during which you typically buy new
              equipment. (select up to three)
            </label>
            {errors.topMonthsToBuy && (
              <RenderError error={errors.topMonthsToBuy} />
            )}
            {Object.values(monthsToBuy).map((month) => (
              <label className="form__checkbox" key={month}>
                <input
                  type="checkbox"
                  value={month}
                  checked={month in formData.topMonthsToBuy}
                  onChange={(event) => handleCheck(event, 'topMonthsToBuy')}
                />
                {month}
              </label>
            ))}
          </div>
          <Submit
            loading={loading}
            handleSubmit={handleSubmit}
            text="Save Section"
            errors={errors}
            disabled={!Boolean(aid)}
          />
          {success && <Success success={success} />}
        </div>
      </div>
    </div>
  );
}

NewEquipment.displayName = 'NewEquipment'; // because WebPack renames the function
export default NewEquipment;
